import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';

import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { CanvasQueryParams } from '~/constants/canvasQueryParams';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCreateWorkspace } from '~/swr-hooks/workspaces/useCreateWorkspace';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';
import { sanitizeEmail } from '~/utils/EmailUtils';
import { getSkipOnboardingEnabledFromQuery } from '~/utils/getSkipOnboardingEnabledFromQuery';
import { getWorkspaceNameFromQuery } from '~/utils/getWorkspaceNameFromQuery';

const getDefaultWorkspaceName = (firstName: string | undefined): string => {
  if (!firstName) {
    return 'My Workspace';
  }

  return `${firstName}'s workspace`;
};

export const useHandleSkipOnboarding = () => {
  const router = useRouter();
  const { data: account } = useAccountContext();
  const { createWorkspace } = useCreateWorkspace();
  const { data: workspaces, isFetched } = useWorkspaces();
  const { showToast } = useToasts();
  const hasRunRef = useRef(false);
  const isMounted = useMountedState();
  const [isLoading, setIsLoading] = useState(false);

  const shouldSkipOnboarding = getSkipOnboardingEnabledFromQuery(router.query);
  const workspaceName = getWorkspaceNameFromQuery(router.query) ?? getDefaultWorkspaceName(account?.firstName);

  useEffect(() => {
    if (!shouldSkipOnboarding) {
      hasRunRef.current = false;
      setIsLoading(false);
    }
  }, [shouldSkipOnboarding]);

  useEffect(() => {
    if (!router.isReady || !shouldSkipOnboarding || !isFetched || !account) {
      return;
    }

    if (hasRunRef.current) {
      return;
    }

    const workspaceCount = workspaces?.length ?? 0;
    const hasWorkspaces = workspaceCount > 0;

    hasRunRef.current = true;
    setIsLoading(true);

    if (hasWorkspaces) {
      const redirectQuery = router.query[QueryParamNames.redirect];
      const explicitRedirect = typeof redirectQuery === 'string' && redirectQuery ? redirectQuery : null;
      const hasCanvasFlowParam =
        !!router.query[QueryParamNames.canvasName] || !!router.query[CanvasQueryParams.canvasId];

      if (!explicitRedirect && hasCanvasFlowParam) {
        const newQuery = { ...router.query };

        delete newQuery[QueryParamNames.skipOnboarding];

        void router
          .replace(
            {
              pathname: router.pathname,
              query: newQuery,
            },
            undefined,
            { shallow: false },
          )
          .finally(() => {
            if (isMounted()) {
              setIsLoading(false);
            }
          });

        return;
      }

      const redirect = explicitRedirect ?? Routes.home;

      void router.replace(redirect).finally(() => {
        if (isMounted()) {
          setIsLoading(false);
        }
      });

      return;
    }

    void createWorkspace(workspaceName, sanitizeEmail(account.email))
      .then(() => {
        const newQuery = { ...router.query };

        delete newQuery[QueryParamNames.skipOnboarding];

        return router.replace(
          {
            pathname: router.pathname,
            query: newQuery,
          },
          undefined,
          { shallow: false },
        );
      })
      .catch((error) => {
        showToast('There was an error creating this workspace. Please try again later.', { color: 'red' });
        reportErrorToBugsnag({
          error,
          context: 'Failed to create workspace while skipping onboarding',
          metadata: {
            data: {
              skipOnboarding: true,
            },
          },
        });
      })
      .finally(() => {
        if (isMounted()) {
          setIsLoading(false);
        }
      });
  }, [
    account,
    createWorkspace,
    isFetched,
    isMounted,
    router,
    shouldSkipOnboarding,
    showToast,
    workspaces?.length,
    workspaceName,
  ]);

  return {
    isLoading,
    shouldSkipOnboarding,
  };
};
